import { useMemo, useState } from "react";
import { Check, ClipboardCopy, FileText, Search, ScrollText } from "lucide-react";

const templates = [
  {
    id: "ppat-ajb",
    group: "PPAT",
    title: "Akta Jual Beli (AJB)",
    aliases: "jual beli tanah bangunan shm shgb",
    fields: ["Nomor akta", "Tanggal akta", "Penjual", "Pembeli", "Objek tanah/bangunan", "Nomor sertifikat", "Harga transaksi", "PBB/NOP", "BPHTB dan PPh", "Saksi"],
    clauses: ["Identitas para pihak", "Riwayat dan dasar hak", "Harga dan cara pembayaran", "Penyerahan objek", "Pernyataan bebas sengketa", "Pajak dan biaya", "Penutup dan tanda tangan"],
    message: "Mohon siapkan data AJB: KTP/KK penjual pembeli, sertifikat asli, PBB/NOP, bukti BPHTB dan PPh, harga transaksi, serta data objek.",
  },
  {
    id: "ppat-hibah",
    group: "PPAT",
    title: "Akta Hibah",
    aliases: "hibah tanah orang tua anak keluarga",
    fields: ["Pemberi hibah", "Penerima hibah", "Hubungan keluarga", "Objek hibah", "Nomor sertifikat", "PBB/NOP", "BPHTB hibah", "Persetujuan pasangan/ahli waris jika perlu"],
    clauses: ["Identitas pemberi dan penerima hibah", "Pernyataan hibah tanpa imbalan", "Penyerahan objek", "Penerimaan hibah", "Pajak dan biaya", "Penutup"],
    message: "Mohon siapkan data hibah: KTP/KK para pihak, sertifikat, PBB, bukti hubungan keluarga bila ada, dan data BPHTB.",
  },
  {
    id: "ppat-apht",
    group: "PPAT",
    title: "APHT",
    aliases: "hak tanggungan bank kredit jaminan",
    fields: ["Debitur", "Kreditur/bank", "Nomor perjanjian kredit", "Nilai tanggungan", "Objek jaminan", "Nomor sertifikat", "SKMHT jika ada", "Roya lama jika ada"],
    clauses: ["Identitas pemberi dan pemegang hak tanggungan", "Dasar perjanjian kredit", "Objek hak tanggungan", "Nilai tanggungan", "Janji-janji APHT", "Pendaftaran HT"],
    message: "Mohon siapkan data APHT: perjanjian kredit, identitas debitur, data bank, sertifikat, nilai hak tanggungan, dan dokumen pendukung bank.",
  },
  {
    id: "ppat-skmht",
    group: "PPAT",
    title: "SKMHT",
    aliases: "surat kuasa membebankan hak tanggungan",
    fields: ["Pemberi kuasa", "Penerima kuasa/bank", "Objek jaminan", "Nomor sertifikat", "Dasar kredit", "Nilai kredit", "Masa berlaku"],
    clauses: ["Pemberian kuasa khusus", "Objek yang dikuasakan", "Kewenangan membebankan hak tanggungan", "Batas waktu dan penggunaan kuasa"],
    message: "Mohon siapkan data SKMHT: KTP pemberi kuasa, data bank/penerima kuasa, sertifikat, dan dasar kredit.",
  },
  {
    id: "ppat-aphb",
    group: "PPAT",
    title: "APHB / Pembagian Hak Bersama",
    aliases: "waris pembagian hak bersama aphb",
    fields: ["Para ahli waris/pemegang hak", "Dasar waris", "Objek tanah", "Nomor sertifikat", "Bagian masing-masing", "Penerima hak akhir", "BPHTB/SSP terkait"],
    clauses: ["Dasar kepemilikan bersama", "Kesepakatan pembagian", "Pelepasan/penyerahan bagian", "Penerimaan hak", "Pajak dan balik nama"],
    message: "Mohon siapkan data APHB: surat waris, KTP/KK ahli waris, sertifikat, PBB, pembagian hak, dan pajak terkait.",
  },
  {
    id: "ppat-pijb-kuasa",
    group: "PPAT",
    title: "PIJB + Kuasa Menjual",
    aliases: "perikatan jual beli kuasa menjual ppjb pijb",
    fields: ["Penjual", "Pembeli", "Objek", "Harga", "Termin pembayaran", "Syarat AJB", "Kuasa menjual", "Sertifikat/PBB"],
    clauses: ["Kesepakatan jual beli", "Harga dan pembayaran", "Kewajiban para pihak", "Syarat peningkatan ke AJB", "Kuasa menjual", "Wanprestasi"],
    message: "Mohon siapkan data PIJB/Kuasa: identitas para pihak, objek, harga, skema pembayaran, sertifikat, dan syarat peningkatan AJB.",
  },
  {
    id: "notaris-kuasa",
    group: "Notarial",
    title: "Surat Kuasa",
    aliases: "kuasa menjual kuasa bank kuasa sertifikat pengecekan",
    fields: ["Pemberi kuasa", "Penerima kuasa", "Tujuan kuasa", "Objek/urusan", "Batas kewenangan", "Masa berlaku", "Hak substitusi jika ada"],
    clauses: ["Identitas pemberi dan penerima kuasa", "Pemberian kewenangan", "Ruang lingkup tindakan", "Hak substitusi", "Penutup"],
    message: "Mohon siapkan data kuasa: KTP pemberi/penerima kuasa, tujuan kuasa, objek/urusan, dan batas kewenangan.",
  },
  {
    id: "notaris-pengakuan-hutang",
    group: "Notarial",
    title: "Pengakuan Hutang",
    aliases: "hutang piutang jaminan cicilan",
    fields: ["Debitur", "Kreditur", "Jumlah hutang", "Jangka waktu", "Cara pembayaran", "Bunga/denda jika ada", "Jaminan", "Saksi"],
    clauses: ["Pengakuan jumlah hutang", "Jadwal pembayaran", "Jaminan", "Kelalaian/wanprestasi", "Domisili hukum", "Penutup"],
    message: "Mohon siapkan data pengakuan hutang: identitas debitur/kreditur, nominal, tempo, skema bayar, dan jaminan bila ada.",
  },
  {
    id: "notaris-pendirian-pt",
    group: "Notarial",
    title: "Pendirian PT",
    aliases: "perseroan terbatas oss ahu modal saham direktur komisaris",
    fields: ["Nama PT", "Kedudukan", "Maksud dan tujuan", "Modal dasar/disetor", "Pemegang saham", "Direktur", "Komisaris", "Alamat usaha"],
    clauses: ["Nama dan tempat kedudukan", "Jangka waktu", "Maksud dan tujuan", "Modal dan saham", "Direksi dan komisaris", "RUPS", "Penutup"],
    message: "Mohon siapkan data pendirian PT: nama PT, bidang usaha, alamat, modal, komposisi saham, direktur, komisaris, KTP/NPWP.",
  },
  {
    id: "notaris-perubahan-pt",
    group: "Notarial",
    title: "Perubahan PT / RUPS",
    aliases: "perubahan perseroan rups saham direksi komisaris alamat modal",
    fields: ["Nama PT", "Nomor akta terakhir", "Agenda perubahan", "Pemegang saham", "Keputusan RUPS", "Direksi/komisaris baru", "Data AHU/OSS"],
    clauses: ["Kehadiran pemegang saham", "Agenda rapat", "Keputusan rapat", "Kuasa pelaporan AHU", "Penutup"],
    message: "Mohon siapkan data perubahan PT/RUPS: akta terakhir, SK AHU, agenda perubahan, daftar pemegang saham, dan susunan pengurus baru.",
  },
  {
    id: "notaris-cv",
    group: "Notarial",
    title: "Pendirian / Perubahan CV",
    aliases: "cv komanditer sekutu aktif pasif",
    fields: ["Nama CV", "Alamat", "Bidang usaha", "Sekutu aktif", "Sekutu pasif", "Modal/kontribusi", "Kewenangan pengurus"],
    clauses: ["Nama dan kedudukan CV", "Maksud dan tujuan", "Sekutu dan modal", "Pengurusan", "Pembagian keuntungan", "Penutup"],
    message: "Mohon siapkan data CV: nama, alamat, bidang usaha, sekutu aktif/pasif, modal, dan KTP/NPWP para pihak.",
  },
  {
    id: "notaris-yayasan-perkumpulan",
    group: "Notarial",
    title: "Yayasan / Perkumpulan",
    aliases: "yayasan perkumpulan pengurus pembina pengawas",
    fields: ["Nama badan", "Alamat", "Maksud tujuan", "Pendiri", "Pembina/pengurus/pengawas", "Kekayaan awal", "Program kegiatan"],
    clauses: ["Nama dan kedudukan", "Maksud dan tujuan", "Kekayaan awal", "Organ yayasan/perkumpulan", "Rapat dan keputusan", "Penutup"],
    message: "Mohon siapkan data yayasan/perkumpulan: nama, alamat, pendiri, susunan pengurus, tujuan kegiatan, dan kekayaan awal.",
  },
  {
    id: "notaris-waris",
    group: "Notarial",
    title: "Keterangan Waris / Pernyataan Waris",
    aliases: "waris ahli waris skb turun waris",
    fields: ["Pewaris", "Tanggal meninggal", "Ahli waris", "Hubungan keluarga", "Harta/objek", "Surat kematian", "KK/KTP", "Saksi keluarga"],
    clauses: ["Data pewaris", "Daftar ahli waris", "Dasar hubungan keluarga", "Pernyataan kebenaran", "Penggunaan surat", "Penutup"],
    message: "Mohon siapkan data waris: surat kematian, KTP/KK pewaris dan ahli waris, buku nikah, akta lahir, objek waris, dan saksi.",
  },
  {
    id: "notaris-perjanjian-kredit",
    group: "Notarial",
    title: "Perjanjian Kredit",
    aliases: "kredit bank pinjaman debitur kreditur",
    fields: ["Debitur", "Kreditur", "Plafond kredit", "Jangka waktu", "Bunga", "Jaminan", "Cara pencairan", "Wanprestasi"],
    clauses: ["Fasilitas kredit", "Jangka waktu", "Bunga dan biaya", "Pembayaran", "Jaminan", "Kelalaian", "Penyelesaian sengketa"],
    message: "Mohon siapkan data perjanjian kredit: identitas pihak, plafond, tenor, bunga, jaminan, dan draft ketentuan bank bila ada.",
  },
  {
    id: "notaris-covernote",
    group: "Notarial",
    title: "Covernote",
    aliases: "covernote bank sertifikat proses balik nama roya ht",
    fields: ["Penerima covernote", "Nama debitur/client", "Objek/sertifikat", "Proses yang dijanjikan", "Estimasi selesai", "Dokumen yang ditahan/diproses"],
    clauses: ["Identitas proses", "Dokumen yang sedang diurus", "Pernyataan penyelesaian", "Batas waktu estimasi", "Penutup"],
    message: "Mohon siapkan data covernote: tujuan surat, nama client/debitur, objek sertifikat, proses yang berjalan, dan estimasi penyelesaian.",
  },
];

const outputTypes = [
  { id: "draft", label: "Kerangka Akta" },
  { id: "checklist", label: "Checklist Data" },
  { id: "message", label: "Pesan Client" },
];

const formatTemplate = (template, output) => {
  if (output === "checklist") {
    return `CHECKLIST DATA - ${template.title}\n\n${template.fields.map((field, index) => `${index + 1}. ${field}`).join("\n")}`;
  }
  if (output === "message") return template.message;
  return `${template.title.toUpperCase()}\n\nJenis: ${template.group}\n\nData utama:\n${template.fields
    .map((field, index) => `${index + 1}. ${field}`)
    .join("\n")}\n\nKerangka klausul:\n${template.clauses.map((clause, index) => `${index + 1}. ${clause}`).join("\n")}`;
};

export default function AktaTemplate() {
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState("Semua");
  const [output, setOutput] = useState("draft");
  const [expandedId, setExpandedId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return templates.filter((template) => {
      const matchesGroup = group === "Semua" || template.group === group;
      const haystack = [template.title, template.group, template.aliases, ...template.fields, ...template.clauses].join(" ").toLowerCase();
      return matchesGroup && (!keyword || haystack.includes(keyword));
    });
  }, [query, group]);

  const copyTemplate = async (template) => {
    await navigator.clipboard.writeText(formatTemplate(template, output));
    setCopiedId(template.id);
    window.setTimeout(() => setCopiedId(null), 1200);
  };

  return (
    <div className="mx-auto max-w-[96rem] space-y-6 p-4 pt-16 sm:p-8 lg:p-10">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="mb-2 text-[11px] uppercase tracking-[0.3em] text-gold">Template Akta</div>
          <h1 className="font-display text-3xl font-medium tracking-tight c-strong sm:text-4xl">Akta PPAT & Notarial</h1>
          <p className="mt-2 max-w-3xl text-sm c-muted">
            Kumpulan kerangka akta berdasarkan pola dokumen lama: AJB, APHT, SKMHT, PIJB, Kuasa, PT, CV, Waris, Covernote, dan lainnya.
          </p>
        </div>
        <div className="grid gap-2 sm:grid-cols-3">
          {outputTypes.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setOutput(item.id)}
              className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                output === item.id ? "border-gold bg-gold/10 text-gold" : "c-border c-text hover:border-gold hover:text-gold"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-[1fr_180px_180px]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 c-muted" />
          <input
            className="w-full rounded-lg border c-border bg-white py-2.5 pl-9 pr-3 text-sm text-ink placeholder:text-[#7A828A] outline-none transition focus:border-gold focus:ring-2 focus:ring-gold/20 dark:bg-[#10171E] dark:text-[#ECE9E2] dark:placeholder:text-[#9AA1A8]"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Cari AJB, APHT, PT, Kuasa, Waris..."
          />
        </div>
        <select
          className="rounded-lg border c-border bg-white px-3 py-2.5 text-sm text-ink outline-none transition focus:border-gold focus:ring-2 focus:ring-gold/20 dark:bg-[#10171E] dark:text-[#ECE9E2]"
          value={group}
          onChange={(event) => setGroup(event.target.value)}
        >
          <option>Semua</option>
          <option>PPAT</option>
          <option>Notarial</option>
        </select>
        <div className="rounded-lg border c-border c-card px-3 py-2.5 text-sm c-muted">{filtered.length} template</div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {filtered.map((template, index) => {
          const expanded = expandedId === template.id;
          return (
            <div key={template.id} className="rounded-xl border c-border c-card card-elev">
              <button
                type="button"
                onClick={() => setExpandedId(expanded ? null : template.id)}
                className="flex w-full items-start gap-4 border-b c-border p-5 text-left"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gold/10 text-gold">
                  <ScrollText className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <h2 className="text-base font-semibold c-strong">{template.title}</h2>
                    <span className="rounded-full bg-ink/[0.04] px-2.5 py-1 text-xs c-muted dark:bg-white/[0.06]">{template.group}</span>
                  </div>
                  <p className="mt-1 text-xs c-muted">{template.fields.length} data utama - {template.clauses.length} klausul</p>
                </div>
              </button>

              {expanded && (
                <div className="space-y-4 p-5">
                  <div>
                    <div className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] c-muted">Data Utama</div>
                    <ol className="space-y-1 text-sm c-text">
                      {template.fields.map((field, fieldIndex) => (
                        <li key={field} className="flex gap-2">
                          <span className="w-5 shrink-0 text-gold">{fieldIndex + 1}.</span>
                          <span>{field}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                  <div>
                    <div className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] c-muted">Kerangka Klausul</div>
                    <ol className="space-y-1 text-sm c-text">
                      {template.clauses.map((clause, clauseIndex) => (
                        <li key={clause} className="flex gap-2">
                          <span className="w-5 shrink-0 text-gold">{clauseIndex + 1}.</span>
                          <span>{clause}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
              )}

              <div className="p-5 pt-4">
                <button
                  type="button"
                  onClick={() => copyTemplate(template)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg border c-border px-3 py-2.5 text-sm font-semibold c-text transition hover:border-gold hover:text-gold"
                >
                  {copiedId === template.id ? <Check className="h-4 w-4" /> : <ClipboardCopy className="h-4 w-4" />}
                  {copiedId === template.id ? "Tersalin" : `Copy ${outputTypes.find((item) => item.id === output)?.label}`}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="rounded-xl border border-dashed c-border p-10 text-center text-sm c-muted">
          Tidak ada template yang cocok.
        </div>
      )}
    </div>
  );
}
